import React, { useEffect, useState } from "react";
import axios from "axios";
import "./signal.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface SignalPoint {
  x: number;
  y: number;
}

interface SignalData {
  [key: string]: number[];
  ch1: number[];
  ch2: number[];
  ch3: number[];
  ch4: number[];
  ch5: number[];
  ch6: number[];
  ch7: number[];
  ch8: number[];
}

interface ChannelDetail {
  channel: string;
  volt: string;
  RPM: string;
  DB: string;
  P2P_mV: string;
  rms_mV: string;
  min_mV: string;
  max_mV: string;
  avg_mV: string;
  timestamp: string;
}

type FormattedData = SignalPoint[];

const SignalGraph: React.FC = () => {
  const emptyChannels: SignalData = {
    ch1: [],
    ch2: [],
    ch3: [],
    ch4: [],
    ch5: [],
    ch6: [],
    ch7: [],
    ch8: [],
  };

  const [signalData, setSignalData] = useState<SignalData>(emptyChannels);
  const [freqData, setFreqData] = useState<SignalData>(emptyChannels);
  const [details, setDetails] = useState<ChannelDetail[]>([]);

  useEffect(() => {
    const fetchData = () => {
      // Time-domain signal
      axios
        .get<SignalData>(
          process.env.REACT_APP_API_URL + "signal/get_signal_data.php"
        )
        .then((response) => {
          const newData = response.data || emptyChannels;
          const cleaned: SignalData = { ...emptyChannels };
          Object.keys(emptyChannels).forEach((key) => {
            cleaned[key] = Array.isArray(newData[key]) ? newData[key] : [];
          });
          setSignalData(cleaned);
        })
        .catch((error) => console.error("Error fetching signal data:", error));

      // Frequency-domain signal
      axios
        .get<SignalData>(
          process.env.REACT_APP_API_URL + "signal/get_signal1sec.php"
        )
        .then((response) => {
          const newData = response.data || emptyChannels;
          const cleaned: SignalData = { ...emptyChannels };
          Object.keys(emptyChannels).forEach((key) => {
            cleaned[key] = Array.isArray(newData[key]) ? newData[key] : [];
          });
          setFreqData(cleaned);
        })
        .catch((error) =>
          console.error("Error fetching signal1sec data:", error)
        );

      // Channel info
      axios
        .get<{ channels: ChannelDetail[] }>(
          process.env.REACT_APP_API_URL + "signal/get_signal_details.php"
        )
        .then((response) => setDetails(response.data.channels || []))
        .catch((error) =>
          console.error("Error fetching signal details:", error)
        );
    };

    fetchData();
    const interval = setInterval(fetchData, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatData = (channelData?: number[]): FormattedData => {
    if (!Array.isArray(channelData) || channelData.length === 0) {
      return Array.from({ length: 6 }, (_, i) => ({ x: i * 200, y: 0 }));
    }

    return channelData.map((value: number, index: number) => ({
      x: index,
      y: value,
    }));
  };

  const renderChannelDetails = (channel: string) => {
    const detail = details.find((d) => d.channel === channel);

    return (
      <div style={{ fontSize: "12px", color: "#000", paddingLeft: "10px" }}>
        <p><strong>Volt:</strong> {detail?.volt || "N/A"} V</p>
        <p><strong>RPM:</strong> {detail?.RPM || "N/A"}</p>
        <p><strong>P2P:</strong> {detail?.P2P_mV || "N/A"} mV</p>
        <p><strong>RMS:</strong> {detail?.rms_mV || "N/A"} mV</p>
        <p><strong>Min:</strong> {detail?.min_mV || "N/A"} mV</p>
        <p><strong>Max:</strong> {detail?.max_mV || "N/A"} mV</p>
        <p><strong>Avg:</strong> {detail?.avg_mV || "N/A"} mV</p>
      </div>
    );
  };

  const channelColors = [
    "#00FF00", // ch1
    "#FFD700", // ch2
    "#00FFFF", // ch3
    "#0000FF", // ch4
    "#FF4500", // ch5
    "#FF69B4", // ch6
    "#ADFF2F", // ch7
    "#FF1493", // ch8
  ];

  return (
    <div style={{ padding: "10px" }}>
      {Object.keys(signalData).map((channel, index) => {
        const channelNumber = index + 1;
        const timeData = formatData(signalData[channel]);
        const freqPlotData = formatData(freqData[channel]);
        const freqColor = channelColors[index % channelColors.length];

        return (
          <div key={channel} style={{ marginBottom: "30px" }}>
            <h3 style={{ color: "#000", marginBottom: "10px" }}>
              Channel {channelNumber}
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr auto",
                gap: "10px",
                alignItems: "center",
              }}
            >
              {/* Time-domain */}
              <div
                style={{
                  backgroundColor: "#000",
                  padding: "5px",
                  borderRadius: "6px",
                  height: "200px",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={timeData}>
                    <CartesianGrid stroke="#222" />
                    <XAxis
                      dataKey="x"
                      stroke="#888"
                      ticks={[0, 200, 400, 600, 800, 1000]}
                    />
                    <YAxis stroke="#888" domain={["auto", "auto"]} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="y"
                      stroke="#00FF00"
                      dot={false}
                      strokeWidth={1.5}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Frequency-domain */}
              <div
                style={{
                  backgroundColor: "#000",
                  padding: "5px",
                  borderRadius: "6px",
                  height: "200px",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={freqPlotData}>
                    <CartesianGrid stroke="#222" />
                    <XAxis
                      dataKey="x"
                      stroke="#888"
                      ticks={[0, 200, 400, 600, 800, 1000]}
                    />
                    <YAxis stroke="#888" />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="y"
                      stroke={freqColor}
                      dot={false}
                      strokeWidth={1.5}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Stats */}
              <div>{renderChannelDetails(channel)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SignalGraph;
